import { ERROR_MESSAGES, PRODUCT_TYPE, PRODUCTS } from 'domain/enums'
import {
  getIndicationsHomeFin,
  getLeadsRequest,
  useGetIndicationsHomeFin,
} from 'lead/application/index'
import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useAuthProvider } from 'shared/application/hooks/useAuthProvider.hook'
import { useQuery } from 'shared/application/hooks/useQuery.hook'
import { logMessageError } from 'shared/infrastructure/exceptions/logError'
import {
  getAgencyInformationWithoutSource,
  getAgentInformation,
  removeLoadingLeads,
  getAgencyPermissionsInformation
} from 'shared/infrastructure/storage'

// eslint-disable-next-line max-lines-per-function
const useLeads = () => {
  const queryPath = useQuery()
  const [isLoading, setLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [leads, setLeads] = useState()
  const [leadsHomeFin, setLeadsHomeFin] = useState()
  const [limit, setLimit] = useState(leads?.cursors?.metadata?.limit || 10)
  const productDefaultFilter = Object.entries(getAgencyPermissionsInformation()).find(([_, isActive]) => isActive)?.[0] || 'HOME_REFI'
  const [filters, setFilters] = useState({
    query: queryPath.get('query') || '',
    productType: queryPath.get('productType') || productDefaultFilter,
    date: queryPath.get('date') || 0,
    stepName: queryPath.get('stepName') || '',
    situation: queryPath.get('situation') || '',
  })
  const auth = useAuthProvider()
  const history = useHistory()
  const numberPage = Number(queryPath.get('page') || 1)
  const [page, setPage] = useState(numberPage)
  const [offset, setOffset] = useState(
    numberPage > 1 ? (numberPage - 1) * limit : 0,
  )
  const [hasFilter, setHasFilter] = useState(false)

  const getLeads = async () => {
    await getLeadsRequest(
      limit,
      offset,
      filters.query,
      filters.productType,
      filters.date,
      filters.stepName,
      filters.situation,
    )
      .then(response => {
        removeLoadingLeads()
        setLeads(response)
      })
      .catch(error => {
        if (error?.response?.status !== 401) {
          logMessageError(
            ERROR_MESSAGES.PROPOSALS_LIST,
            JSON.stringify(error?.response),
          )
        }
        if (error?.response?.status === 401) {
          auth.logout()
        }
        setHasError(true)
      })
      .finally(() => setLoading(false))
  }

  const onError = () => {
    setHasError(true)
  }
  const { getIndicationsRequest } = useGetIndicationsHomeFin({ onError })
  const getLeadsHomeFin = async () => {
    await getIndicationsRequest({
      params: {
        page,
        perPage: 10,
        searchText: filters.query,
        status: filters.situation,
        stepName: filters.stepName,
        days: parseInt(filters.date),
      },
      agency: getAgencyInformationWithoutSource(),
      agent: getAgentInformation(),
    })
      .then(response => {
        setLeadsHomeFin(response)
      })
      .catch(error => {
        if (error?.response?.status !== 401) {
          logMessageError(
            ERROR_MESSAGES.PROPOSALS_LIST,
            JSON.stringify(error?.response),
          )
        }
        if (error?.response?.status === 401) {
          auth.logout()
        }
        setHasError(true)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    setLoading(true)
    setHasError(false)
    setHasFilter(false)
    if (filters.productType === PRODUCTS.HOME_FIN) {
      getLeadsHomeFin()
    } else {
      getLeads()
    }
    Object.keys(filters).map(key => {
      if (
        key !== 'productType' &&
        filters[key] != '' &&
        filters[key] !== undefined
      ) {
        setHasFilter(() => true)
      }
    })
  }, [page, limit, filters])

  const { search } = useLocation()

  const resetPage = () => {
    setLimit(leads?.cursors?.metadata.limit || limit)
    setPage(1)
    setOffset(0)
    queryPath.delete('page')
  }

  const setParamsUrl = params => {
    const queryParam = Object.fromEntries(
      Object.entries(params).filter(
        key => key[1] !== null && key[1] !== undefined,
      ),
    )

    const replaceParam = Object.entries(queryParam).filter(item => {
      const itemEmpty = item[1] !== '' && item[1] !== 0
      if (itemEmpty) queryPath.set(item[0], item[1])
      if (!itemEmpty) queryPath.delete(item[0])
      return history.push(`?${queryPath.toString()}`)
    })

    const queryFormatter = Object.entries(queryParam)
      .map(item => item.join(':'))
      .join('&')
      .replace(/:/g, '=')

    if (search !== '') {
      return replaceParam
    }

    return history.push(`?${queryFormatter}`)
  }

  const changePage = (newPage, currentLimit) => {
    setParamsUrl({ page: newPage })

    setPage(newPage)
    setOffset(newPage > 1 ? (newPage - 1) * limit : 0)
    setLimit(currentLimit)
  }

  const changeSearch = ({ query, date }) => {
    resetPage()

    setParamsUrl({ query, date })

    const queryValue = queryPath.get('query') || query
    const dateValue = queryPath.get('date') || date

    if (filters.query !== queryValue || filters.date !== dateValue) {
      setFilters({
        ...filters,
        query: queryValue,
        date: dateValue,
      })
    }
  }

  const changeFilters = (productType, stepName, situation) => {
    if (
      filters.productType !== productType ||
      filters.stepName !== stepName ||
      filters.situation !== situation
    ) {
      setFilters({
        ...filters,
        productType,
        stepName,
        situation,
      })
    }

    setParamsUrl({ productType, stepName, situation })
  }

  return {
    isLoading,
    hasError,
    leads,
    leadsHomeFin,
    page,
    limit,
    changePage,
    changeSearch,
    changeFilters,
    filters,
    hasFilter,
  }
}

export { useLeads }
